/**
 * Detect Supabase auth errors that mean the stored refresh token is unusable.
 * Typical causes: session revoked server-side, token rotated elsewhere, or stale cookies.
 */
export function isInvalidRefreshTokenError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;

  const record = error as {
    message?: string;
    code?: string;
    status?: number;
  };

  const message = String(record.message ?? "");
  const code = String(record.code ?? "");

  if (code === "refresh_token_not_found" || code === "refresh_token_already_used") {
    return true;
  }

  if (/invalid refresh token/i.test(message) || /refresh token not found/i.test(message)) {
    return true;
  }

  // Supabase Auth API returns 400 for invalid refresh token requests.
  if (record.status === 400 && /refresh/i.test(message)) {
    return true;
  }

  return false;
}

/**
 * Detect authentication, session invalidation, or authorization errors across Supabase/PostgREST.
 * Matches:
 *  - HTTP 401 Unauthorized or 403 Forbidden
 *  - PostgREST JWT errors: PGRST301 (expired), PGRST302 (invalid), PGRST303
 *  - Postgres 42501 (insufficient privilege / permission denied)
 *  - Refresh token errors (revoked, already used, not found)
 *  - Common error messages: "Access denied", "JWT expired", "invalid claim", "not authenticated"
 */
export function isAuthenticationError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;

  const record = error as {
    message?: string;
    code?: string;
    status?: number;
  };

  const code = String(record.code ?? "");
  const message = String(record.message ?? "");
  const status = record.status;

  if (status === 401 || status === 403) return true;

  if (
    code === "PGRST301" ||
    code === "PGRST302" ||
    code === "PGRST303" ||
    code === "42501" ||
    code === "refresh_token_not_found" ||
    code === "refresh_token_already_used"
  ) {
    return true;
  }

  return (
    /access denied/i.test(message) ||
    /jwt expired/i.test(message) ||
    /invalid claim/i.test(message) ||
    /unauthorized/i.test(message) ||
    /forbidden/i.test(message) ||
    /session.*not found/i.test(message) ||
    /invalid refresh token/i.test(message) ||
    /refresh token not found/i.test(message) ||
    /not authenticated/i.test(message)
  );
}
